import React, { useState, useEffect, useRef, useMemo } from 'react';
import { optimizeLinkedIn } from '../services/openaiService';
import { parseResumeFile } from '../services/resumeParserService';
import { useUser } from '../contexts/UserContext';
import { useStudioTheme } from '../hooks/useStudioTheme';
import Header from './Header';
import './LinkedInOptimizerPage.css';
import './LinkedInOptimizerPage.theme.css';
import ProductSidebar from './ProductSidebar';
import { UserIcon, FileTextIcon } from '../electron/icons';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

/* Skills arrive as a comma string from the profile, an array from SerpApi,
   and a free-text blob from the optimizer. Normalize all three to chips. */
const toSkillList = (val) => {
  if (Array.isArray(val)) return val.map(s => String(s).trim()).filter(Boolean);
  // The resume parser can hand back a CATEGORISED object —
  // { technical: 'Go, Rust', tools: 'Docker' } — so flatten those values
  // before splitting, or a parsed resume silently yields zero skills.
  if (val && typeof val === 'object') {
    return toSkillList(Object.values(val).filter(v => typeof v === 'string').join(', '));
  }
  if (typeof val === 'string') {
    return val
      .split(/[,•|\n]/)
      .map(s => s.replace(/^[-–\s]+/, '').trim())
      .filter(Boolean);
  }
  return [];
};

/* "2h ago" / "3d ago" — the cache runs a week, so day granularity is enough. */
const agoLabel = (ts) => {
  if (!ts) return '';
  const mins = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
  if (!Number.isFinite(mins) || mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

/* "https://www.linkedin.com/in/x/" → "linkedin.com/in/x". A third of the
   URL box was scheme boilerplate; the backend normalises the short form
   identically, so this is display-only sugar with no behaviour change. */
const shortLinkedInUrl = (raw = '') =>
  String(raw).trim().replace(/^https?:\/\/(www\.)?/i, '').replace(/\/+$/, '');

const initials = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || '👤';

/* Section header used down both control columns. `hint` is carried as a
   tooltip rather than a visible sub-line — it kept the headers explanatory
   without adding a second row of text to every section. */
/* `accent` is an "R, G, B" triplet fed to CSS as --accent, mirroring the
   Chrome extension's per-feature colour system: a 0.14-alpha tint, a
   0.3-alpha border and the icon at full strength. Passing channel values
   rather than a hex lets one prop drive every alpha variation. */
/* Non-interactive replicas of LinkedIn's own section controls. Rendered as
   <span> rather than <button> so they can't be tabbed to or clicked, and
   aria-hidden so screen readers don't announce affordances that don't exist.
   Purely to make the preview read as the real page. */
const DecoIcons = ({ addable = true }) => (
  <span className="li-pv-deco" aria-hidden="true">
    {addable && (
      <svg viewBox="0 0 24 24" width="19" height="19" fill="none"
           stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M12 5v14M5 12h14" />
      </svg>
    )}
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none"
         stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  </span>
);

const Section = ({ icon: Icon, title, hint, tag, accent }) => (
  <div
    className="lio-sec"
    title={hint || undefined}
    style={accent ? { '--accent': accent } : undefined}
  >
    <span className="lio-sec-icon"><Icon size={15} /></span>
    <h3>{title}</h3>
    {tag}
  </div>
);

const LinkedInOptimizerPage = () => {
  const { user } = useUser();
  const themeClass = useStudioTheme();
  // Header owns the auth modal; we only ever need to open it.
  const [, setShowAuthModal] = useState(false);

  /* ── Profile data shared by both panels ─────────────────────────── */
  const [profileData, setProfileData] = useState(null);
  const [profileAvatarUrl, setProfileAvatarUrl] = useState(null);

  /* ── Live LinkedIn profile (SerpApi) ────────────────────────────── */
  const [linkedInUrl, setLinkedInUrl] = useState('');
  const [livePro, setLivePro] = useState(null);          // structured public profile
  const [liveFetchedAt, setLiveFetchedAt] = useState(null);
  const [liveCached, setLiveCached] = useState(false);
  const [isFetchingLive, setIsFetchingLive] = useState(false);
  const [liveError, setLiveError] = useState('');
  const [hasExtension, setHasExtension] = useState(false);
  const [isSyncingExt, setIsSyncingExt] = useState(false);
  const autoFetchedRef = useRef(false);

  /* ── Headshot panel state ───────────────────────────────────────── */
  const [headshotSource, setHeadshotSource] = useState('profile'); // 'profile' | 'upload'
  const [uploadedPhoto, setUploadedPhoto] = useState(null);
  const [uploadedPreviewUrl, setUploadedPreviewUrl] = useState(null);
  const [generatedHeadshot, setGeneratedHeadshot] = useState(null);
  const [isGeneratingHeadshot, setIsGeneratingHeadshot] = useState(false);
  const [headshotError, setHeadshotError] = useState('');
  const photoInputRef = useRef(null);
  /* Style catalogue comes from the backend so the picker and the prompt
     builder can't drift apart. The fallback list keeps the UI usable if the
     request fails — ids must match server.js HEADSHOT_STYLES. */
  const [headshotStyles, setHeadshotStyles] = useState([
    { id: 'corporate', label: 'Corporate' },
    { id: 'executive', label: 'Executive' },
    { id: 'businessCasual', label: 'Business casual' },
    { id: 'startup', label: 'Startup' },
    { id: 'creative', label: 'Creative' },
    { id: 'monochrome', label: 'Black & white' },
  ]);
  const [headshotStyle, setHeadshotStyle] = useState('corporate');
  const [headshotNotes, setHeadshotNotes] = useState('');
  const NOTES_MAX = 300;

  /* ── Optimizer panel state ──────────────────────────────────────── */
  const [targetRole, setTargetRole] = useState('');
  const [targetIndustry, setTargetIndustry] = useState('');

  /* ── Editable profile fields (middle column) ──────────────────────
     These are the single source of truth for BOTH the AI rewrite and the
     preview, so what the user sees on the right is exactly what gets
     optimized. They're pre-filled from whichever source is selected —
     the LinkedIn sync, the saved Terview profile, or a parsed resume —
     but any field the user types into is marked dirty and never
     overwritten by a later re-fill. */
  const BLANK_FIELDS = {
    fullName: '', headline: '', about: '', location: '',
    skills: [],       // string[]
    experience: [],   // { title, company, duration }[]
    education: [],    // { school, degree, years }[]
  };
  // Factory, not the shared literal — reusing BLANK_FIELDS would alias the
  // same skills/experience/education arrays across every reset.
  const blankFields = () => ({ ...BLANK_FIELDS, skills: [], experience: [], education: [] });
  const [fields, setFields] = useState(BLANK_FIELDS);
  const [dirty, setDirty] = useState({});
  const [skillDraft, setSkillDraft] = useState('');
  /* A real profile carries 40-60 skills. Rendered in full they took 58% of
     the column and pushed Experience, Education and Target role ~690px below
     a fold with nothing indicating they were there. Collapsed by default,
     same "Show all N" pattern the preview's Experience section uses. */
  const [showAllSkills, setShowAllSkills] = useState(false);
  const SKILL_PREVIEW_COUNT = 12;

  /* Switching source wipes the form first, so the user never sees the
     previous source's values sitting under the new selection. Picking
     "Resume" therefore shows an empty set until a file has been parsed;
     the auto-fill effect repopulates as soon as data arrives. */
  const selectSource = (src) => {
    setResumeSource(src);
    setFields(blankFields());
    setDirty({});
    setSkillDraft('');
    setError('');
  };

  const setField = (key, value) => {
    setFields(f => ({ ...f, [key]: value }));
    setDirty(d => ({ ...d, [key]: true }));
  };

  /* ── Repeating-row helpers ───────────────────────────────────────
     Experience and Education are lists, so each needs add / edit one
     cell / remove. Skills are a flat tag list with a comma-splitting
     adder, so "React, Node, AWS" pasted in one go becomes three tags. */
  const addRow = (key, blank) => setField(key, [...fields[key], blank]);
  const updateRow = (key, i, patch) =>
    setField(key, fields[key].map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const removeRow = (key, i) => setField(key, fields[key].filter((_, idx) => idx !== i));

  const addSkills = (raw) => {
    const incoming = String(raw)
      .split(',')
      .map(t => t.trim())
      .filter(Boolean);
    if (!incoming.length) return;
    const existing = new Set(fields.skills.map(t => t.toLowerCase()));
    const merged = [...fields.skills];
    incoming.forEach(t => {
      if (existing.has(t.toLowerCase())) return;
      existing.add(t.toLowerCase());
      merged.push(t);
    });
    setField('skills', merged);
    setSkillDraft('');
  };

  const [resumeSource, setResumeSource] = useState('profile');     // 'profile' | 'upload'
  const [uploadedResumeName, setUploadedResumeName] = useState('');
  const [parsedResume, setParsedResume] = useState(null);
  const [isParsingResume, setIsParsingResume] = useState(false);
  const resumeInputRef = useRef(null);

  const [results, setResults] = useState({});
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState({});

  /* Preview toggle — 'current' shows the real profile, 'optimized' swaps in
     the AI rewrite section by section. */
  const [previewMode, setPreviewMode] = useState('current');
  const [openTips, setOpenTips] = useState({});
  /* LinkedIn caps Experience at 5 rows and Education at 2, then offers
     "Show all N". Ours expands in place rather than linking away — this is a
     preview, so hiding entries behind a dead link would lose the user's data. */
  const [showAll, setShowAll] = useState({ experience: false, education: false });

  /* ── Fetch profile on mount ──────────────────────────────────────── */
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/api/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (cancelled || !data?.profile) return;
        setProfileData(data.profile);
        setProfileAvatarUrl(data.profile.avatarUrl || null);
        if (data.profile.jobTitle) setTargetRole(prev => prev || data.profile.jobTitle);
        if (data.profile.linkedIn) {
          setLinkedInUrl(prev => prev || shortLinkedInUrl(data.profile.linkedIn));
        }
        // If no saved avatar, default the headshot source to "upload".
        if (!data.profile.avatarUrl) setHeadshotSource('upload');
      } catch (err) {
        console.error('Failed to fetch profile:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => () => {
    if (uploadedPreviewUrl) URL.revokeObjectURL(uploadedPreviewUrl);
  }, [uploadedPreviewUrl]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/headshot/styles`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!r.ok) return;                     // keep the built-in fallback
        const d = await r.json();
        if (cancelled || !d?.styles?.length) return;
        setHeadshotStyles(d.styles);
        if (d.defaultStyle) setHeadshotStyle(prev => prev || d.defaultStyle);
      } catch { /* offline → fallback list stands */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  /* ───────────────────── Live LinkedIn lookup ───────────────────── */

  const fetchLiveProfile = async (urlArg, { refresh = false } = {}) => {
    const url = (urlArg ?? linkedInUrl).trim();
    if (!user) { setShowAuthModal(true); return; }
    if (!url) { setLiveError('Paste your LinkedIn profile URL first.'); return; }

    setIsFetchingLive(true);
    setLiveError('');
    try {
      const r = await fetch(`${API_BASE_URL}/api/linkedin/fetch-profile`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url, refresh }),
      });
      // A dev-proxy 404 or a crashed backend answers with HTML, not JSON —
      // r.json() would then throw "Unexpected token '<'", which tells the
      // user nothing. Read as text first and report what actually happened.
      const body = await r.text();
      let data;
      try { data = JSON.parse(body); } catch {
        throw new Error(
          r.status === 404
            ? 'The /api/linkedin/fetch-profile route is missing — restart the backend server.'
            : `Server returned ${r.status} instead of JSON. Check the backend logs.`
        );
      }
      if (!r.ok) throw new Error(data?.message || 'Could not load that profile.');
      setLivePro(data.profile || null);
      setLiveFetchedAt(data.fetchedAt || null);
      setLiveCached(!!data.cached);
    } catch (err) {
      setLivePro(null);
      setLiveError(err.message || 'Could not load that profile.');
    } finally {
      setIsFetchingLive(false);
    }
  };

  /* ── Extension sync (full profile) ──────────────────────────────
     SerpApi tops out at name + headline + a truncated About. The Chrome
     extension reads the real page in the user's own session and POSTs it
     to /api/linkedin/import-profile, which writes the SAME cache row the
     SerpApi path uses — so once it lands we just re-read that row. */
  useEffect(() => {
    const mark = () => setHasExtension(true);
    // The bridge sets this attribute at document_start and also fires an event.
    if (document.documentElement.getAttribute('data-terview-ext')) mark();
    window.addEventListener('terview-ext-ready', mark);

    const onMessage = (e) => {
      if (e.source !== window || !e.data?.terviewPage) return;
      if (e.data.type !== 'EXT_LINKEDIN_SYNC_RESULT') return;
      setIsSyncingExt(false);
      const r = e.data.payload || {};
      if (!r.ok) { setLiveError(r.error || 'Could not read your LinkedIn profile.'); return; }
      setLiveError('');
      // Re-read the freshly imported row (refresh:false — it's seconds old).
      fetchLiveProfile(linkedInUrl);
    };
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('terview-ext-ready', mark);
      window.removeEventListener('message', onMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedInUrl]);

  const syncViaExtension = () => {
    const url = linkedInUrl.trim();
    if (!user) { setShowAuthModal(true); return; }
    if (!url) { setLiveError('Add your LinkedIn profile URL first.'); return; }
    setLiveError('');
    setIsSyncingExt(true);
    window.postMessage({
      terviewExt: true,
      type: 'EXT_LINKEDIN_SYNC',
      payload: { token: localStorage.getItem('token'), apiBase: API_BASE_URL, profileUrl: url },
    }, '*');
    // The extension opens a tab, reads it, and posts back. If the user closes
    // that tab or the extension is wedged, don't spin forever.
    setTimeout(() => setIsSyncingExt(v => {
      if (v) setLiveError('LinkedIn sync timed out — make sure you are signed in to LinkedIn and try again.');
      return false;
    }), 60000);
  };

  // Auto-load once when the profile already has a saved LinkedIn URL. Cached
  // server-side for a week, so this is free on every visit after the first.
  useEffect(() => {
    if (autoFetchedRef.current) return;
    if (!user || !linkedInUrl) return;
    autoFetchedRef.current = true;
    fetchLiveProfile(linkedInUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, linkedInUrl]);

  /* ───────────────────────────── Headshot ───────────────────────── */

  const handlePhotoSelect = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setHeadshotError('Please choose an image file (PNG, JPG, WEBP).');
      return;
    }
    if (uploadedPreviewUrl) URL.revokeObjectURL(uploadedPreviewUrl);
    setUploadedPhoto(file);
    setUploadedPreviewUrl(URL.createObjectURL(file));
    setHeadshotError('');
    setGeneratedHeadshot(null);
  };

  const handleGenerateHeadshot = async () => {
    if (!user) { setShowAuthModal(true); return; }
    setHeadshotError('');
    setGeneratedHeadshot(null);

    const form = new FormData();
    if (headshotSource === 'upload') {
      if (!uploadedPhoto) {
        setHeadshotError('Choose a photo to upload first.');
        return;
      }
      form.append('photo', uploadedPhoto);
    } else {
      if (!profileAvatarUrl) {
        setHeadshotError('No profile picture on file — upload a photo instead.');
        return;
      }
      const fname = profileAvatarUrl.split('/').pop();
      form.append('avatarPath', fname);
    }
    form.append('style', headshotStyle);
    if (headshotNotes.trim()) form.append('notes', headshotNotes.trim());

    setIsGeneratingHeadshot(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/headshot/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: form,
      });
      const data = await r.json();
      if (!r.ok || !data?.image) throw new Error(data?.message || 'Headshot generation failed.');
      setGeneratedHeadshot(data.image);
    } catch (err) {
      setHeadshotError(err.message || 'Headshot generation failed.');
    } finally {
      setIsGeneratingHeadshot(false);
    }
  };

  const downloadHeadshot = () => {
    if (!generatedHeadshot) return;
    const a = document.createElement('a');
    a.href = generatedHeadshot;
    const base = (profileData?.fullName || user?.name || 'headshot').replace(/\s+/g, '_');
    a.download = `${base}_headshot.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const headshotSourceReady =
    headshotSource === 'profile' ? !!profileAvatarUrl : !!uploadedPhoto;

  /* Normalise whatever the source gave us into the field set's row shape. */
  const toExpRows = (rows = []) => rows
    .filter(e => e && (e.title || e.position || e.company))
    .map(e => ({
      title: e.title || e.position || '',
      company: e.company || '',
      duration: e.duration || '',
    }));

  const toEduRows = (rows = []) => rows
    .filter(e => e && (e.school || e.institution || e.degree))
    .map(e => ({
      school: e.school || e.institution || '',
      degree: [e.degree, e.field].filter(Boolean).join(', ') || e.degree || '',
      years: e.years || e.year || '',
    }));

  /* ───────────────────────────── Optimizer ──────────────────────── */

  const handleResumeUpload = async (file) => {
    if (!file) return;
    setError('');
    setUploadedResumeName(file.name);
    setIsParsingResume(true);
    try {
      const parsed = await parseResumeFile(file, '', []);
      if (!parsed) throw new Error('Could not read the resume.');
      const adapted = {
        fullName: parsed.personalInfo?.name || '',
        email:    parsed.personalInfo?.email || '',
        phone:    parsed.personalInfo?.phone || '',
        location: parsed.personalInfo?.location || '',
        linkedIn: parsed.personalInfo?.linkedin || '',
        portfolio: parsed.personalInfo?.portfolio || '',
        professionalSummary: parsed.summary || '',
        workExperience: parsed.workExperience || [],
        education: parsed.education || [],
        skills: parsed.skills || '',
        projects: parsed.projects || [],
        certifications: parsed.certifications || '',
      };
      // Clear edits so the freshly parsed resume fills every field rather
      // than being blocked by the dirty flags from the previous source.
      setFields(blankFields());
      setDirty({});
      setParsedResume(adapted);
    } catch (err) {
      setError(`Resume parse failed: ${err.message}`);
      setUploadedResumeName('');
    } finally {
      setIsParsingResume(false);
    }
  };

  const handleOptimize = async () => {
    if (!user) { setShowAuthModal(true); return; }
    if (!targetRole.trim()) {
      setError('Please enter your target role.');
      return;
    }
    if (!fields.headline.trim() && !fields.about.trim()) {
      setError('Add a headline or an About section before optimizing.');
      return;
    }
    // Built from the edited fields, not the raw source — the user may have
    // corrected what the sync or the resume parser got wrong.
    const activeProfile = {
      fullName: fields.fullName,
      jobTitle: fields.headline,
      location: fields.location,
      professionalSummary: fields.about,
      skills: fields.skills.join(', '),
      workExperience: fields.experience.map(e => ({
        position: e.title, company: e.company, duration: e.duration,
      })),
      education: fields.education.map(e => ({
        institution: e.school, degree: e.degree, year: e.years,
      })),
    };

    setIsOptimizing(true);
    setError('');
    setResults({});
    try {
      // Shared LinkedIn Optimizer + Headshot pool — 5 free uses per user
      // across both actions. Deduct before invoking OpenAI; active
      // subscribers bypass on the backend.
      const dr = await fetch(`${API_BASE_URL}/api/linkedin-optimizer/deduct-credit`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ kind: 'optimize' }),
      });
      if (!dr.ok) {
        const d = await dr.json().catch(() => ({}));
        throw new Error(d?.error || 'Out of LinkedIn Optimizer credits — upgrade to continue.');
      }
      // Feed the live public profile in as extra context when we have it —
      // the rewrite is sharper when the AI can see what's actually published.
      const data = await optimizeLinkedIn(
        livePro?.profileUrl || linkedInUrl,
        targetRole,
        targetIndustry,
        '',
        activeProfile
      );
      setResults(data.optimizations || {});
      // Flip the preview to the rewrite — that's the payoff of the click.
      if (data.optimizations && Object.keys(data.optimizations).length) {
        setPreviewMode('optimized');
      }
    } catch (err) {
      setError(err.message || 'Failed to optimize. Please try again.');
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleCopy = (sectionId, text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied((prev) => ({ ...prev, [sectionId]: true }));
    setTimeout(() => setCopied((prev) => ({ ...prev, [sectionId]: false })), 2000);
  };

  const hasResults = Object.keys(results).length > 0;
  const optimizerReady =
    targetRole.trim() && (fields.headline.trim() || fields.about.trim());
  const skillLimitHit = fields.skills.length >= 50;

  /* ───────────────── Preview model ───────────────────────────────
     Layered: live LinkedIn data (what the world sees) wins, the saved
     profile / parsed resume fills the gaps Google's snippet never has
     (full experience list, education, skills).                       */
  const fallback = resumeSource === 'upload' && parsedResume ? parsedResume : profileData;

  const base = useMemo(() => {
    const f = fallback || {};
    const l = livePro || {};
    const expFromProfile = (f.workExperience || [])
      .filter(e => e && (e.company || e.position))
      .map(e => ({
        title: e.position || e.title || '',
        company: e.company || '',
        duration: e.duration || '',
        location: e.location || '',
      }));
    const eduFromProfile = (f.education || [])
      .filter(e => e && (e.institution || e.degree))
      .map(e => ({
        school: e.institution || e.school || '',
        degree: [e.degree, e.field].filter(Boolean).join(', '),
        years: e.year || e.years || '',
      }));

    return {
      // Text fields come from the editable set so the preview updates as the
      // user types; structured rows still come from the source, since those
      // carry logos and dates the flattened textarea can't hold.
      fullName: fields.fullName || l.fullName || f.fullName || user?.name || 'Your Name',
      headline: fields.headline || l.headline || f.jobTitle || '',
      company: l.currentCompany || f.currentEmployer || '',
      location: fields.location || l.location || f.location || '',
      connections: l.connections || '',
      about: fields.about || l.about || f.professionalSummary || '',
      // Rows come from the edited field set so the preview tracks every
      // change. Logos/employment type aren't editable, so they're matched
      // back on by company name from whatever the sync returned.
      // Blank rows are a half-finished edit, not profile content — an empty
      // card would otherwise render as a placeholder "Role" in the preview.
      experience: fields.experience.some(e => e.title || e.company)
        ? fields.experience.filter(e => e.title || e.company).map(e => {
            const src = (l.experience || []).find(x => x.company === e.company) || {};
            return { ...e, employmentType: src.employmentType, tenure: src.tenure,
                     location: src.location, workMode: src.workMode, logo: src.logo || null };
          })
        : (l.experience?.length ? l.experience : expFromProfile),
      education: fields.education.some(e => e.school || e.degree)
        ? fields.education.filter(e => e.school || e.degree).map(e => {
            const src = (l.education || []).find(x => x.school === e.school) || {};
            return { ...e, grade: src.grade, activities: src.activities, logo: src.logo || null };
          })
        : (l.education?.length ? l.education : eduFromProfile),
      skills: fields.skills.length
        ? fields.skills
        : (l.skills?.length ? l.skills : toSkillList(f.skills)),
      photo: l.photo || null,
      banner: l.banner || null,
      profileUrl: l.profileUrl || linkedInUrl || '',
    };
  }, [livePro, fallback, user, linkedInUrl, fields]);

  /* Fill the field set from the selected source. Runs whenever the source
     data changes, and on demand from the "Fill from…" buttons. Fields the
     user has typed into are preserved unless `force` is set. */
  const fillFields = React.useCallback((force = false) => {
    // "Resume" means the resume — not the LinkedIn sync layered over it.
    // Only the "Profile data" source falls back through livePro.
    const fromResume = resumeSource === 'upload';
    const l = fromResume ? {} : (livePro || {});
    const f = (fromResume ? parsedResume : profileData) || {};
    const pick = (...vals) => vals.find(v => v && String(v).trim()) || '';
    const next = {
      fullName: pick(l.fullName, f.fullName, user?.name),
      // A parsed resume has no headline field, so fall back to the most
      // recent job title — the same thing LinkedIn defaults a headline to.
      headline: pick(l.headline, f.jobTitle, f.workExperience?.[0]?.position, f.workExperience?.[0]?.title),
      about: pick(l.about, f.professionalSummary),
      location: pick(l.location, f.location),
      skills: l.skills?.length ? l.skills : toSkillList(f.skills),
      experience: l.experience?.length ? toExpRows(l.experience) : toExpRows(f.workExperience || []),
      education: l.education?.length ? toEduRows(l.education) : toEduRows(f.education || []),
    };
    setFields(prev => {
      const out = { ...prev };
      Object.keys(next).forEach(k => {
        const v = next[k];
        const empty = Array.isArray(v) ? !v.length : !v;
        if (empty) return;
        if (force || !dirty[k]) out[k] = v;
      });
      return out;
    });
    if (force) setDirty({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livePro, profileData, parsedResume, resumeSource, user, dirty]);

  // Auto-fill on first arrival of any source data.
  useEffect(() => { fillFields(false); }, [livePro, profileData, parsedResume, resumeSource]); // eslint-disable-line react-hooks/exhaustive-deps

  const showOptimized = previewMode === 'optimized' && hasResults;
  const opt = (id) => (showOptimized ? results[id] : null);

  // The optimizer returns skills as either a keyword list ("React, Node, AWS")
  // or a paragraph of advice. Chips only make sense for the former — anything
  // sentence-length is rendered as a note instead of being chopped into
  // nonsense pills.
  const optimizedSkills = (() => {
    const raw = opt('skills')?.optimized;
    if (!raw) return null;
    const list = toSkillList(raw);
    if (!list.length || list.some(s => s.length > 45)) return { chips: null, note: raw };
    return { chips: list, note: '' };
  })();

  const view = {
    headline: opt('headline')?.optimized || base.headline,
    about: opt('summary')?.optimized || base.about,
    experienceNote: opt('experience')?.optimized || '',
    educationNote: opt('education')?.optimized || '',
    skills: optimizedSkills?.chips || base.skills,
    skillsNote: optimizedSkills?.note || '',
  };

  // Generated headshot is the whole point of the left panel — show it on the
  // preview the moment it exists.
  const avatarSrc =
    generatedHeadshot ||
    (profileAvatarUrl ? `${API_BASE_URL}${profileAvatarUrl}` : null) ||
    base.photo;

  const toggleTips = (id) => setOpenTips(p => ({ ...p, [id]: !p[id] }));

  /* Small renderer for the issues / tips / keywords the optimizer returns —
     kept out of the main flow so the preview reads like a real profile, one
     click away when the user wants the reasoning. */
  const SectionCoach = ({ id }) => {
    const r = results[id];
    if (!showOptimized || !r) return null;
    const has = r.issues?.length || r.tips?.length || r.keywords?.length;
    if (!has) return null;
    const open = !!openTips[id];
    return (
      <div className="li-coach">
        <button type="button" className="li-coach-toggle" onClick={() => toggleTips(id)}>
          <span className="li-coach-dot" />
          {open ? 'Hide AI notes' : 'Why this works'}
          <span className={`li-coach-chevron ${open ? 'is-open' : ''}`}>⌄</span>
        </button>
        {open && (
          <div className="li-coach-body">
            {r.issues?.length > 0 && (
              <div className="li-coach-block">
                <h5>⚠️ What was holding it back</h5>
                <ul>{r.issues.map((x, i) => <li key={i}>{x}</li>)}</ul>
              </div>
            )}
            {r.tips?.length > 0 && (
              <div className="li-coach-block">
                <h5>💡 Pro tips</h5>
                <ul>{r.tips.map((x, i) => <li key={i}>{x}</li>)}</ul>
              </div>
            )}
            {r.keywords?.length > 0 && (
              <div className="li-coach-block">
                <h5>🔑 Keywords to include</h5>
                <div className="li-keyword-chips">
                  {r.keywords.map((k, i) => <span key={i} className="li-keyword-chip">{k}</span>)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const SectionHead = ({ id, title, addable = true }) => (
    <div className="li-pv-section-head">
      <h2 className="li-pv-sec-title">{title}</h2>
      <div className="li-pv-head-actions">
        {showOptimized && results[id] && <span className="li-ai-pill">AI</span>}
        {/* Copy appears only once this section actually has an AI rewrite to
            take — before that there's nothing to lift into LinkedIn. */}
        {showOptimized && results[id] && (
        <button
          type="button"
          className="li-pv-icon-btn"
          onClick={() => handleCopy(
            id,
            id === 'skills' ? (view.skillsNote || view.skills.join(', '))
              : id === 'summary' ? view.about
              : id === 'headline' ? view.headline
              : id === 'experience' ? (view.experienceNote || base.experience.map(e => `${e.title} — ${e.company}`).join('\n'))
              : (view.educationNote || base.education.map(e => `${e.degree} — ${e.school}`).join('\n'))
          )}
          title="Copy section"
        >
          {copied[id] ? 'Copied' : 'Copy'}
        </button>
        )}
        <DecoIcons addable={addable} />
      </div>
    </div>
  );

  /* Open-to-work banner mirrors LinkedIn's — driven by the real work-mode
     preferences saved on the Terview profile, so it only shows when true. */
  const openToModes = [
    profileData?.openToOnsite && 'On-site',
    profileData?.openToHybrid && 'Hybrid',
    profileData?.openToRemote && 'Remote',
  ].filter(Boolean);
  const pronouns =
    profileData?.pronouns && profileData.pronouns !== 'Decline to answer'
      ? profileData.pronouns
      : '';


  return (
    <div className={`neural-glass-homepage lio-page ${themeClass}`}>
      <Header setShowAuthModal={setShowAuthModal} />
      <ProductSidebar user={user} setShowAuthModal={setShowAuthModal} />

      <main className="lio-main">
        {/* Theme switch — parks itself beside the header pill; see
            `.cls-theme-dock` in studioTheme.css. */}
        <div className="lio-three-col">
          {/* ═══════════ COLUMN 1 — Headshot ═══════════ */}
          <aside className="lio-col lio-col--shot">
            {/* Pinned above .lio-col-body for the same reason as the footer —
                the column title scrolled away and left the panel unlabelled. */}
            <div className="lio-col-head">
              <Section
                icon={UserIcon}
                title="Headshot"
                hint="Turn any photo into a studio portrait"
                accent="34, 211, 238"
              />
            </div>
            <div className="lio-col-body">
            <div className="lio-seg lio-seg--sm">
              <button
                type="button"
                className={`lio-seg-btn ${headshotSource === 'profile' ? 'is-active' : ''}`}
                onClick={() => setHeadshotSource('profile')}
                disabled={!profileAvatarUrl || isGeneratingHeadshot}
              >
                Profile picture
              </button>
              <button
                type="button"
                className={`lio-seg-btn ${headshotSource === 'upload' ? 'is-active' : ''}`}
                onClick={() => setHeadshotSource('upload')}
                disabled={isGeneratingHeadshot}
              >
                Upload photo
              </button>
            </div>

            <div className="lio-shot-stack">
              <div className="lio-shot-slot">
                <span className="lio-shot-label">Source</span>
                {headshotSource === 'profile' && profileAvatarUrl ? (
                  <img src={`${API_BASE_URL}${profileAvatarUrl}`} alt="Profile" />
                ) : headshotSource === 'upload' && uploadedPreviewUrl ? (
                  <img src={uploadedPreviewUrl} alt="Upload preview" />
                ) : (
                  <button
                    type="button"
                    className="lio-shot-empty"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={isGeneratingHeadshot || headshotSource === 'profile'}
                  >
                    <span>📁</span>
                    {headshotSource === 'upload' ? 'Choose photo' : 'No photo'}
                  </button>
                )}
                {headshotSource === 'upload' && uploadedPreviewUrl && (
                  <button
                    type="button"
                    className="lio-shot-replace"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={isGeneratingHeadshot}
                  >
                    Replace
                  </button>
                )}
                <input
                  ref={photoInputRef} type="file" accept="image/*" hidden
                  onChange={(e) => handlePhotoSelect(e.target.files?.[0])}
                />
              </div>

              <div className="lio-shot-slot">
                <span className="lio-shot-label">Generated</span>
                {generatedHeadshot ? (
                  <img src={generatedHeadshot} alt="Generated headshot" />
                ) : (
                  <div className={`lio-shot-empty is-static ${isGeneratingHeadshot ? 'is-busy' : ''}`}>
                    {isGeneratingHeadshot
                      ? <><span className="li-spinner" />Generating…</>
                      : <><span>✨</span>Result appears here</>}
                  </div>
                )}
              </div>
            </div>

            {headshotError && <div className="lio-error">{headshotError}</div>}

            <div className="lio-field lio-style-field">
              <label>Style</label>
              <div className="lio-style-grid">
                {headshotStyles.map(st => (
                  <button
                    key={st.id}
                    type="button"
                    className={`lio-style-chip ${headshotStyle === st.id ? 'is-active' : ''}`}
                    onClick={() => setHeadshotStyle(st.id)}
                    disabled={isGeneratingHeadshot}
                    title={st.description || st.label}
                  >
                    {st.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="lio-field">
              <label>Custom notes</label>
              <textarea
                rows={2}
                value={headshotNotes}
                maxLength={NOTES_MAX}
                onChange={(e) => setHeadshotNotes(e.target.value)}
                placeholder="e.g. navy suit, plain white background, slightly wider crop"
                disabled={isGeneratingHeadshot}
              />
            </div>

            </div>

            {/* Same pinned-footer treatment as the fields column, and the same
                .lio-launch-btn class — the two columns' committing actions were
                a different size, weight and fill from each other. */}
            <div className="lio-col-foot">
              <div className="lio-btn-row">
                <button
                  type="button"
                  className="lio-launch-btn lio-grow"
                  onClick={handleGenerateHeadshot}
                  disabled={isGeneratingHeadshot || !headshotSourceReady}
                  title={headshotSourceReady ? undefined : 'Add a photo first'}
                >
                  {isGeneratingHeadshot ? 'Generating…' : '✨ Generate'}
                </button>
                {generatedHeadshot && (
                  <button type="button" className="lio-ghost-btn" onClick={downloadHeadshot} title="Download">⬇</button>
                )}
              </div>
            </div>
          </aside>

          {/* ═══════════ COLUMN 2 — Profile fields ═══════════ */}
          <section className="lio-col lio-col--fields">
            <div className="lio-col-head">
              <Section
                icon={FileTextIcon}
                title="Profile fields"
                hint="Edit anything — the preview updates as you type"
                accent="193, 61, 253"
              />
            </div>
            <div className="lio-col-body">

            {/* Where the fields get filled from. Re-clicking force-refills,
                overwriting edits — that's the point of the button. */}
            <div className="lio-seg lio-seg--sm">
              <button
                type="button"
                className={`lio-seg-btn ${resumeSource === 'profile' ? 'is-active' : ''}`}
                onClick={() => selectSource('profile')}
                disabled={isOptimizing}
              >
                Profile data
              </button>
              <button
                type="button"
                className={`lio-seg-btn ${resumeSource === 'upload' ? 'is-active' : ''}`}
                onClick={() => selectSource('upload')}
                disabled={isOptimizing}
              >
                Resume
              </button>
            </div>

            {resumeSource === 'upload' && (
              <div className="lio-btn-row">
                <input
                  ref={resumeInputRef} type="file" accept=".pdf,.doc,.docx" hidden
                  onChange={(e) => handleResumeUpload(e.target.files?.[0])}
                />
                <button
                  type="button" className="lio-ghost-btn lio-grow"
                  onClick={() => resumeInputRef.current?.click()}
                  disabled={isParsingResume || isOptimizing}
                >
                  {/* Wrapped: text-overflow needs a block box, and the
                      button is an inline-flex container whose anonymous text
                      node can't take it. */}
                  <span className="lio-btn-label">
                    {isParsingResume ? 'Parsing…' : uploadedResumeName ? `Replace (${uploadedResumeName})` : '📄 Choose PDF / DOCX'}
                  </span>
                </button>
                {parsedResume && !isParsingResume && <span className="lio-ok">✓</span>}
              </div>
            )}

            <div className="lio-field lio-field--first">
              <label>Full name</label>
              <input
                type="text" value={fields.fullName}
                onChange={(e) => setField('fullName', e.target.value)}
                placeholder="Your name" disabled={isOptimizing}
              />
            </div>

            <div className="lio-field">
              <label>Current headline *</label>
              <input
                type="text" value={fields.headline}
                onChange={(e) => { setField('headline', e.target.value); setError(''); }}
                placeholder="e.g. Software Engineer at Tech Corp" disabled={isOptimizing}
              />
            </div>

            <div className="lio-field">
              <label>About section *</label>
              <textarea
                rows={4} value={fields.about}
                onChange={(e) => { setField('about', e.target.value); setError(''); }}
                placeholder="Describe your professional background, skills, and career goals…"
                disabled={isOptimizing}
              />
            </div>

            <div className="lio-field">
              <label>Location</label>
              <input
                type="text" value={fields.location}
                onChange={(e) => setField('location', e.target.value)}
                placeholder="e.g. San Francisco, CA" disabled={isOptimizing}
              />
            </div>

            <div className="lio-field">
              <label>Skills {fields.skills.length > 0 && <span className="lio-count">{fields.skills.length}</span>}</label>
              {fields.skills.length > 0 && (
                <div className="lio-tags">
                  {(showAllSkills ? fields.skills : fields.skills.slice(0, SKILL_PREVIEW_COUNT)).map((skill, i) => (
                    <span key={`${skill}-${i}`} className="lio-tag-chip">
                      {skill}
                      <button
                        type="button"
                        onClick={() => setField('skills', fields.skills.filter((_, idx) => idx !== i))}
                        disabled={isOptimizing}
                        aria-label={`Remove ${skill}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {fields.skills.length > SKILL_PREVIEW_COUNT && (
                <button
                  type="button"
                  className="lio-more-btn"
                  onClick={() => setShowAllSkills(v => !v)}
                >
                  {showAllSkills
                    ? 'Show fewer'
                    : `Show all ${fields.skills.length} skills`}
                </button>
              )}

              <div className="lio-add-row">
                <input
                  type="text"
                  value={skillDraft}
                  onChange={(e) => setSkillDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); addSkills(skillDraft); }
                  }}
                  placeholder="Add skills, comma-separated"
                  disabled={isOptimizing || skillLimitHit}
                />
                <button
                  type="button"
                  className="lio-add-btn"
                  onClick={() => addSkills(skillDraft)}
                  disabled={isOptimizing || !skillDraft.trim() || skillLimitHit}
                  title="Add skill"
                >
                  +
                </button>
              </div>
            </div>

            <div className="lio-field">
              <label>
                Experience {fields.experience.length > 0 && <span className="lio-count">{fields.experience.length}</span>}
              </label>
              {fields.experience.map((row, i) => (
                <div key={i} className="lio-row-card">
                  {/* Remove sits BESIDE the title input, not absolutely over
                      it — overlapping the field made it read as floating
                      debris and collided with long titles. */}
                  <div className="lio-row-head">
                    <input
                      type="text" value={row.title}
                      onChange={(e) => updateRow('experience', i, { title: e.target.value })}
                      placeholder="Job title" disabled={isOptimizing}
                    />
                    <button
                      type="button" className="lio-row-remove"
                      onClick={() => removeRow('experience', i)}
                      disabled={isOptimizing} aria-label="Remove role" title="Remove role"
                    >
                      ×
                    </button>
                  </div>
                  <div className="lio-row-split">
                    <input
                      type="text" value={row.company}
                      onChange={(e) => updateRow('experience', i, { company: e.target.value })}
                      placeholder="Company" disabled={isOptimizing}
                    />
                    <input
                      type="text" value={row.duration}
                      onChange={(e) => updateRow('experience', i, { duration: e.target.value })}
                      placeholder="2020 – Present" disabled={isOptimizing}
                    />
                  </div>
                </div>
              ))}
              <button
                type="button" className="lio-add-more"
                onClick={() => addRow('experience', { title: '', company: '', duration: '' })}
                disabled={isOptimizing}
              >
                + Add role
              </button>
            </div>

            <div className="lio-field">
              <label>
                Education {fields.education.length > 0 && <span className="lio-count">{fields.education.length}</span>}
              </label>
              {fields.education.map((row, i) => (
                <div key={i} className="lio-row-card">
                  <div className="lio-row-head">
                    <input
                      type="text" value={row.school}
                      onChange={(e) => updateRow('education', i, { school: e.target.value })}
                      placeholder="School" disabled={isOptimizing}
                    />
                    <button
                      type="button" className="lio-row-remove"
                      onClick={() => removeRow('education', i)}
                      disabled={isOptimizing} aria-label="Remove school" title="Remove school"
                    >
                      ×
                    </button>
                  </div>
                  <div className="lio-row-split">
                    <input
                      type="text" value={row.degree}
                      onChange={(e) => updateRow('education', i, { degree: e.target.value })}
                      placeholder="Degree, field" disabled={isOptimizing}
                    />
                    <input
                      type="text" value={row.years}
                      onChange={(e) => updateRow('education', i, { years: e.target.value })}
                      placeholder="2022 – 2024" disabled={isOptimizing}
                    />
                  </div>
                </div>
              ))}
              <button
                type="button" className="lio-add-more"
                onClick={() => addRow('education', { school: '', degree: '', years: '' })}
                disabled={isOptimizing}
              >
                + Add school
              </button>
            </div>

            <div className="lio-field">
              <label>Target role *</label>
              <input
                type="text" value={targetRole}
                onChange={(e) => { setTargetRole(e.target.value); setError(''); }}
                placeholder="Senior Software Engineer" disabled={isOptimizing}
              />
            </div>
            <div className="lio-field">
              <label>Target industry (optional)</label>
              <input
                type="text" value={targetIndustry}
                onChange={(e) => setTargetIndustry(e.target.value)}
                placeholder="FinTech, SaaS…" disabled={isOptimizing}
              />
            </div>

            </div>

            {/* The CTA sits outside .lio-col-body so it stays visible while the
                fields scroll behind it — it was previously the last child of the
                scroller and fell below the fold on every realistic profile. */}
            <div className="lio-col-foot">
              {error && <div className="lio-error">{error}</div>}
              <button
                className="lio-launch-btn"
                onClick={handleOptimize}
                disabled={isOptimizing || !optimizerReady}
                title={optimizerReady ? undefined : 'Add a headline and target role first'}
              >
                {isOptimizing ? (
                  <span className="li-loading">
                    <span className="typing-indicator"><span></span><span></span><span></span></span>
                    Optimizing…
                  </span>
                ) : '✨ Optimize Profile'}
              </button>
            </div>
          </section>

          {/* ═══════════ COLUMN 3 — Live preview ═══════════ */}
          <section className="lio-col lio-col--preview">
            <div className="lio-panel-header">
              {/* Short label: the panel's content is self-evidently the
                  LinkedIn preview, and the ~70px saved goes to the URL box,
                  which was truncating to "linkedin.com/in/rajk…". */}
              <span className="lio-panel-title">Preview</span>
              <div className="lio-seg">
                <button
                  type="button"
                  className={`lio-seg-btn ${!showOptimized ? 'is-active' : ''}`}
                  onClick={() => setPreviewMode('current')}
                >
                  Current
                </button>
                <button
                  type="button"
                  className={`lio-seg-btn ${showOptimized ? 'is-active' : ''}`}
                  onClick={() => hasResults && setPreviewMode('optimized')}
                  disabled={!hasResults}
                  title={hasResults ? '' : 'Run the optimizer to unlock'}
                >
                  ✨ AI Optimized
                </button>
              </div>
              {/* Source controls live here rather than in the left column:
                  they act ON the preview, so they belong above it. */}
              <div className="lio-panel-tools">
                <div className="lio-url-row">
                  <input
                    type="text"
                    value={linkedInUrl}
                    onChange={(e) => { setLinkedInUrl(e.target.value); setLiveError(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') fetchLiveProfile(); }}
                    placeholder="linkedin.com/in/your-name"
                    disabled={isFetchingLive}
                    // The box is narrow in the header, so expose the full
                    // value on hover rather than only the truncated head.
                    title={linkedInUrl || undefined}
                    onBlur={() => {
                      const short = shortLinkedInUrl(linkedInUrl);
                      if (short && short !== linkedInUrl) setLinkedInUrl(short);
                    }}
                  />
                  <button
                    type="button"
                    className="lio-ghost-btn lio-ghost-btn--sm"
                    onClick={() => fetchLiveProfile(undefined, { refresh: !!livePro })}
                    disabled={isFetchingLive || !linkedInUrl.trim()}
                    title={livePro ? 'Re-fetch from Google' : 'Load profile'}
                  >
                    {isFetchingLive ? <span className="li-spinner" /> : livePro ? '↻' : 'Load'}
                  </button>
                </div>
                <button
                  type="button"
                  className="lio-btn lio-btn--sm lio-btn--accent lio-sync-btn"
                  onClick={syncViaExtension}
                  disabled={!hasExtension || isSyncingExt || !linkedInUrl.trim()}
                  title={hasExtension
                    ? 'Opens your LinkedIn profile and reads it in your own browser'
                    : 'Install the Terview Chrome extension to enable full sync'}
                >
                  {isSyncingExt ? <><span className="li-spinner" />Reading…</> : '⟳ Sync full profile'}
                </button>
                {base.profileUrl && (
                  <a className="lio-btn lio-btn--sm lio-btn--quiet" href={base.profileUrl} target="_blank" rel="noreferrer">
                    Open
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
                         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M7 17 17 7M9 7h8v8" />
                    </svg>
                  </a>
                )}
              </div>
            </div>

            <div className={`li-pv ${showOptimized ? 'is-optimized' : ''}`}>
              {isFetchingLive && <div className="li-pv-loading"><span className="li-spinner" /> Reading your public profile…</div>}

              {/* Live-data status bar. Sits at the TOP because "am I looking
                  at my real LinkedIn or at my Terview profile?" is the first
                  question this panel has to answer. */}
              {!isFetchingLive && !livePro && (
                <div className="li-pv-notice">
                  <span className="li-pv-notice-icon">🔗</span>
                  <div className="li-pv-notice-text">
                    <strong>Showing your Terview profile</strong>
                    <span>
                      {linkedInUrl
                        ? 'Load your LinkedIn URL to pull in what recruiters actually see.'
                        : 'Add your LinkedIn URL to pull in what recruiters actually see.'}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="li-pv-notice-btn"
                    onClick={() => {
                      if (!linkedInUrl.trim()) { document.querySelector('.lio-url-row input')?.focus(); return; }
                      // Prefer the extension — it returns the whole profile
                      // rather than the three fields Google exposes.
                      if (hasExtension) syncViaExtension(); else fetchLiveProfile();
                    }}
                  >
                    {!linkedInUrl.trim() ? 'Add URL' : hasExtension ? 'Sync profile' : 'Load profile'}
                  </button>
                </div>
              )}

              {/* Sync/lookup failures surface here — the controls that cause
                  them now live in the panel header, so this is the only place
                  the user would see them. */}
              {liveError && !isFetchingLive && (
                <div className="li-pv-notice li-pv-notice--err">
                  <span className="li-pv-notice-icon">⚠️</span>
                  <div className="li-pv-notice-text"><strong>{liveError}</strong></div>
                </div>
              )}

              {/* ── Identity card ── */}
              <section className="li-pv-card li-pv-card--id">
                <div className="li-pv-banner">
                  {base.banner && <img className="li-pv-banner-img" src={base.banner} alt="" />}
                  {showOptimized && <span className="li-pv-banner-tag">✨ AI-optimized preview</span>}
                  <span className="li-pv-banner-edit">✎</span>
                </div>

                <div className="li-pv-avatar">
                  {avatarSrc
                    ? <img src={avatarSrc} alt={base.fullName} />
                    : <span className="li-pv-avatar-fallback">{initials(base.fullName)}</span>}
                  {generatedHeadshot && <span className="li-pv-avatar-badge" title="AI headshot applied">✨</span>}
                </div>

                <div className="li-pv-id-body">
                  <div className="li-pv-id-main">
                    <h1 className="li-pv-name">
                      {base.fullName}
                      {pronouns && <span className="li-pv-pronouns">{pronouns}</span>}
                    </h1>
                    <p className={`li-pv-headline ${showOptimized && results.headline ? 'is-ai' : ''}`}>
                      {view.headline || <span className="li-pv-muted">Add a headline to stand out</span>}
                    </p>
                    <p className="li-pv-meta">
                      {base.location || 'Location not public'}
                      <span className="li-pv-dot">·</span>
                      <span className="li-pv-link">Contact info</span>
                    </p>
                    <p className="li-pv-net">
                      {base.connections ? `${base.connections} connections` : 'Connections not public'}
                    </p>

                    <div className="li-pv-cta-row">
                      <button type="button" className="li-pv-cta li-pv-cta--primary">Open to</button>
                      <button type="button" className="li-pv-cta">Add section</button>
                      <button type="button" className="li-pv-cta">Enhance profile</button>
                      <button type="button" className="li-pv-cta li-pv-cta--round" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                          <circle cx="5" cy="12" r="1.9" />
                          <circle cx="12" cy="12" r="1.9" />
                          <circle cx="19" cy="12" r="1.9" />
                        </svg>
                      </button>
                    </div>

                    {openToModes.length > 0 && (
                      <div className="li-pv-otw">
                        <div className="li-pv-otw-title">Open to work</div>
                        <div className="li-pv-otw-sub">
                          {targetRole || base.headline || 'Roles'} · {openToModes.join(' · ')}
                        </div>
                        <span className="li-pv-link">Show details</span>
                      </div>
                    )}
                  </div>

                  {base.company && (
                    <div className="li-pv-company">
                      <div className="li-pv-company-logo">{base.company.charAt(0).toUpperCase()}</div>
                      <span>{base.company}</span>
                    </div>
                  )}
                </div>
              </section>

              {/* ── About ── */}
              <section className="li-pv-card">
                <SectionHead id="summary" title="About" addable={false} />
                {view.about
                  ? <p className={`li-pv-about ${showOptimized && results.summary ? 'is-ai' : ''}`}>{view.about}</p>
                  : <p className="li-pv-empty">No About section yet — the optimizer will write one.</p>}
                <SectionCoach id="summary" />
              </section>

              {/* ── Experience ── */}
              <section className="li-pv-card">
                <SectionHead id="experience" title="Experience" />
                {base.experience.length > 0 ? (
                  <ul className="li-pv-list">
                    {(showAll.experience ? base.experience : base.experience.slice(0, 5)).map((e, i) => (
                      <li key={i} className="li-pv-item">
                        <div className="li-pv-logo">
                          {e.logo
                            ? <img src={e.logo} alt="" />
                            : (e.company || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="li-pv-item-body">
                          {/* Composed the way LinkedIn does: each line joins its
                              two halves with a middot, and collapses cleanly
                              when the tail is missing (e.g. no employment type
                              on a founder role). */}
                          <div className="li-pv-item-title">{e.title || 'Role'}</div>
                          <div className="li-pv-item-sub">
                            {[e.company, e.employmentType].filter(Boolean).join(' · ')}
                          </div>
                          {(e.duration || e.tenure) && (
                            <div className="li-pv-item-meta">
                              {[e.duration, e.tenure].filter(Boolean).join(' · ')}
                            </div>
                          )}
                          {(e.location || e.workMode) && (
                            <div className="li-pv-item-meta">
                              {[e.location, e.workMode].filter(Boolean).join(' · ')}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="li-pv-empty">No roles found — add them in your profile.</p>
                )}
                {view.experienceNote && (
                  <div className="li-pv-ai-note">
                    <div className="li-pv-ai-note-head">✨ Rewrite your experience like this</div>
                    <p>{view.experienceNote}</p>
                  </div>
                )}
                <SectionCoach id="experience" />
                {base.experience.length > 5 && (
                  <button
                    type="button"
                    className="li-pv-showall"
                    onClick={() => setShowAll(p => ({ ...p, experience: !p.experience }))}
                  >
                    {showAll.experience ? 'Show less' : `Show all ${base.experience.length} experiences`} →
                  </button>
                )}
              </section>

              {/* ── Skills ── */}
              <section className="li-pv-card">
                <SectionHead id="skills" title="Skills" />
                {view.skills.length > 0 ? (
                  <div className="li-pv-skills">
                    {view.skills.slice(0, 24).map((s, i) => (
                      <span key={i} className={`li-pv-skill ${optimizedSkills?.chips ? 'is-ai' : ''}`}>{s}</span>
                    ))}
                  </div>
                ) : (
                  <p className="li-pv-empty">No skills listed.</p>
                )}
                {view.skillsNote && (
                  <div className="li-pv-ai-note">
                    <div className="li-pv-ai-note-head">✨ Skills to add</div>
                    <p>{view.skillsNote}</p>
                  </div>
                )}
                <SectionCoach id="skills" />
              </section>

              {/* ── Education ── */}
              <section className="li-pv-card">
                <SectionHead id="education" title="Education" />
                {base.education.length > 0 ? (
                  <ul className="li-pv-list">
                    {(showAll.education ? base.education : base.education.slice(0, 2)).map((e, i) => (
                      <li key={i} className="li-pv-item">
                        <div className="li-pv-logo li-pv-logo--edu">
                          {e.logo ? <img src={e.logo} alt="" /> : '🎓'}
                        </div>
                        <div className="li-pv-item-body">
                          <div className="li-pv-item-title">{e.school || 'School'}</div>
                          {e.degree && <div className="li-pv-item-sub">{e.degree}</div>}
                          {e.years && <div className="li-pv-item-meta">{e.years}</div>}
                          {e.grade && <div className="li-pv-item-meta">{e.grade}</div>}
                          {e.activities && <div className="li-pv-item-meta">{e.activities}</div>}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="li-pv-empty">No education listed.</p>
                )}
                {view.educationNote && (
                  <div className="li-pv-ai-note">
                    <div className="li-pv-ai-note-head">✨ Suggested wording</div>
                    <p>{view.educationNote}</p>
                  </div>
                )}
                <SectionCoach id="education" />
                {base.education.length > 2 && (
                  <button
                    type="button"
                    className="li-pv-showall"
                    onClick={() => setShowAll(p => ({ ...p, education: !p.education }))}
                  >
                    {showAll.education ? 'Show less' : `Show all ${base.education.length} educations`} →
                  </button>
                )}
              </section>

              {livePro && (
                <div className="li-pv-hint">
                  {liveCached && liveFetchedAt ? `Synced ${agoLabel(liveFetchedAt)}. ` : ''}
                  {livePro.source === 'extension'
                    ? 'Read directly from your LinkedIn profile. Anything LinkedIn hides behind “Show all” is filled from your Terview profile.'
                    : 'From Google’s index — that covers your name, headline and part of your About only. Experience, education and skills are filled from your Terview profile until you run a full sync.'}
                </div>
              )}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default LinkedInOptimizerPage;
